import React from 'react';
import { Shield, Award, Star, CheckCircle, Phone, Building, Users, Clock } from 'lucide-react';

const CredentialsShowcase = ({ language = 'en' }) => {
  const text = {
    en: {
      title: 'Our Credentials & Success Stories',
      subtitle: 'Licensed professionals with a proven track record of helping families',
      license: 'California Bail License',
      licenseNumber: 'License #1N13041',
      bbb: 'BBB A+ Rating',
      bbbDescription: '10+ years of A+ rating with zero unresolved complaints',
      experience: 'Years of Experience',
      experienceDescription: 'Serving Sacramento Valley families since 2009',
      families: 'Families Helped',
      familiesDescription: 'Successfully reunited families across Central California',
      response: '24/7 Response Time',
      responseDescription: 'Average phone answer time under 10 seconds',
      insurance: 'Fully Bonded & Insured',
      insuranceDescription: 'Protected by $1M professional liability coverage',
      testimonialTitle: 'What Sets Us Apart',
      testimonials: [
        {
          quote: "They answered at 2 AM and had my son home by 6 AM. Professional service when we needed it most.",
          author: "Jennifer M., Sacramento",
          rating: 5
        },
        {
          quote: "Clear pricing, no surprises, payment plan that worked for us. Treated us with dignity during a tough time.",
          author: "Robert L., Stockton", 
          rating: 5
        },
        {
          quote: "Spanish-speaking agent walked me through everything step by step. Cultural understanding made all the difference.",
          author: "Maria G., Modesto",
          rating: 5
        }
      ],
      callToAction: 'Experience the Difference Professional Service Makes'
    },
    es: {
      title: 'Nuestras Credenciales e Historias de Éxito',
      subtitle: 'Profesionales licenciados con un historial comprobado de ayudar a familias',
      license: 'Licencia de Fianza de California',
      licenseNumber: 'Licencia #1N13041',
      bbb: 'Calificación A+ del BBB',
      bbbDescription: '10+ años de calificación A+ sin quejas sin resolver',
      experience: 'Años de Experiencia',
      experienceDescription: 'Sirviendo a familias del Valle de Sacramento desde 2009',
      families: 'Familias Ayudadas',
      familiesDescription: 'Reunimos exitosamente familias en todo California Central',
      response: 'Tiempo de Respuesta 24/7',
      responseDescription: 'Tiempo promedio de respuesta telefónica bajo 10 segundos',
      insurance: 'Completamente Garantizado y Asegurado',
      insuranceDescription: 'Protegido por cobertura de responsabilidad profesional de $1M',
      testimonialTitle: 'Lo Que Nos Distingue',
      testimonials: [
        {
          quote: "Contestaron a las 2 AM y tuvieron a mi hijo en casa para las 6 AM. Servicio profesional cuando más lo necesitábamos.",
          author: "Jennifer M., Sacramento",
          rating: 5
        },
        {
          quote: "Precios claros, sin sorpresas, plan de pagos que funcionó para nosotros. Nos trataron con dignidad durante un momento difícil.",
          author: "Robert L., Stockton",
          rating: 5
        },
        {
          quote: "El agente que habla español me explicó todo paso a paso. La comprensión cultural hizo toda la diferencia.",
          author: "Maria G., Modesto",
          rating: 5
        }
      ],
      callToAction: 'Experimente la Diferencia que Hace el Servicio Profesional'
    }
  };

  const currentText = text?.[language] || text?.en;

  const credentials = [
    {
      icon: Shield,
      title: currentText?.license,
      subtitle: currentText?.licenseNumber,
      description: language === 'es' ?'Licenciados y regulados por el Departamento de Seguros de California' :'Licensed and regulated by California Department of Insurance',
      color: 'blue'
    },
    {
      icon: Award,
      title: currentText?.bbb,
      subtitle: 'Since 2014',
      description: currentText?.bbbDescription,
      color: 'green'
    },
    {
      icon: Building,
      title: currentText?.insurance,
      subtitle: '$1M Coverage',
      description: currentText?.insuranceDescription,
      color: 'purple'
    }
  ];

  const stats = [
    {
      icon: Clock,
      number: '15+',
      label: currentText?.experience,
      description: currentText?.experienceDescription
    },
    {
      icon: Users,
      number: '2,000+',
      label: currentText?.families,
      description: currentText?.familiesDescription
    },
    {
      icon: Phone,
      number: '<10s',
      label: currentText?.response,
      description: currentText?.responseDescription
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">
          {currentText?.title}
        </h2>
        <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
          {currentText?.subtitle}
        </p>
      </div>
      {/* Credentials Grid */}
      <div className="grid lg:grid-cols-3 gap-8 mb-16">
        {credentials?.map((credential, index) => (
          <div key={index} className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden hover:shadow-2xl transition-shadow">
            <div className={`bg-gradient-to-r ${
              credential?.color === 'blue' ? 'from-blue-600 to-blue-700' :
              credential?.color === 'green'? 'from-green-600 to-green-700' : 'from-purple-600 to-purple-700'
            } text-white p-6 text-center`}>
              <credential.icon className="w-16 h-16 mx-auto mb-4" aria-hidden="true" />
              <h3 className="text-xl font-bold mb-2">{credential?.title}</h3>
              <p className="text-lg font-semibold opacity-90">{credential?.subtitle}</p>
            </div>
            <div className="p-6">
              <p className="text-gray-700 leading-relaxed">{credential?.description}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Statistics */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 border border-gray-200 mb-16">
        <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">
          {language === 'es' ? 'Nuestros Números Hablan' : 'Our Numbers Speak'}
        </h3>
        
        <div className="grid md:grid-cols-3 gap-8">
          {stats?.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <stat.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" aria-hidden="true" />
                <div className="text-4xl font-black text-blue-600 mb-2">{stat?.number}</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{stat?.label}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{stat?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Success Stories */}
      <div className="mb-16">
        <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
          {currentText?.testimonialTitle}
        </h3>
        
        <div className="grid lg:grid-cols-3 gap-8">
          {currentText?.testimonials?.map((testimonial, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
              <div className="flex mb-4">
                {[...Array(testimonial?.rating)]?.map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" aria-hidden="true" />
                ))}
              </div>
              <blockquote className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                "{testimonial?.quote}"
              </blockquote>
              <footer className="border-t border-gray-200 pt-4">
                <div className="font-bold text-gray-900">{testimonial?.author}</div>
              </footer>
            </div>
          ))}
        </div>
      </div>
      {/* Professional Associations */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 mb-16">
        <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
          {language === 'es' ? 'Asociaciones Profesionales' : 'Professional Associations'}
        </h3>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          {[
            {
              name: language === 'es' ? 'Asociación de Fianzas de California' : 'California Bail Agents Association',
              status: language === 'es' ? 'Miembro Activo' : 'Active Member'
            },
            {
              name: language === 'es' ? 'Asociación Nacional de Agentes de Fianza' : 'National Bail Agents Association',
              status: language === 'es' ? 'Miembro Certificado' : 'Certified Member'
            },
            {
              name: language === 'es' ? 'Cámara de Comercio de Sacramento' : 'Sacramento Chamber of Commerce',
              status: language === 'es' ? 'Negocio Acreditado' : 'Accredited Business'
            },
            {
              name: 'Better Business Bureau',
              status: language === 'es' ? 'Calificación A+' : 'A+ Rating'
            }
          ]?.map((association, index) => (
            <div key={index} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-3" aria-hidden="true" />
              <h4 className="font-bold text-gray-900 mb-2 text-sm">{association?.name}</h4>
              <p className="text-xs text-gray-600">{association?.status}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Final CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl p-12 text-center">
        <h3 className="text-3xl font-bold mb-6">
          {currentText?.callToAction}
        </h3>
        <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
          {language === 'es' ?'Cuando su familia necesita ayuda profesional de fianza, elija agentes con credenciales comprobadas, experiencia local y un compromiso con el servicio excepcional.' :'When your family needs professional bail help, choose agents with proven credentials, local experience, and a commitment to exceptional service.'
          }
        </p>
        
        <div className="flex flex-col lg:flex-row gap-4 justify-center items-center">
          <a 
            href="tel:+19168330052"
            className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all inline-flex items-center"
          >
            <Phone className="w-6 h-6 mr-3" aria-hidden="true" />
            {language === 'es' ? 'Llamar Agentes Profesionales' : 'Call Professional Agents'}: (916) 833-0052
          </a>
          
          <div className="text-blue-100 font-semibold">
            {language === 'es' ? 'Disponible 24/7 - Respuesta Garantizada' : 'Available 24/7 - Response Guaranteed'}
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-6 text-center">
          <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
            <Shield className="w-8 h-8 mx-auto mb-2" aria-hidden="true" />
            <div className="font-bold">{language === 'es' ? 'Licenciado' : 'Licensed'}</div>
          </div>
          <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
            <Award className="w-8 h-8 mx-auto mb-2" aria-hidden="true" />
            <div className="font-bold">{language === 'es' ? 'Acreditado' : 'Accredited'}</div>
          </div>
          <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
            <CheckCircle className="w-8 h-8 mx-auto mb-2" aria-hidden="true" />
            <div className="font-bold">{language === 'es' ? 'Confiable' : 'Trusted'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CredentialsShowcase;